import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce, isEqual } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { looksLikeSecret } from '@/Domain/helper/secretShapes';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const OTHER_TEXT_DEBOUNCE_MS = 800;

/**
 * The two free-text fields. They are the only fields written to the cache on every keystroke rather
 * than inside `onMutate`, which is why they need their own rollback baseline below.
 */
const OTHER_TEXT_FIELDS = ['external_apps_other_text', 'deployment_other_text'] as const;
type OtherTextField = (typeof OTHER_TEXT_FIELDS)[number];
const isOtherTextField = (key: string): key is OtherTextField =>
  (OTHER_TEXT_FIELDS as readonly string[]).includes(key);

/**
 * GettingStarted onboarding state, backed by the
 * `/v1/api/getting_started_progress` endpoint.
 *
 * - Progress is scoped per `ChatImplementation`; each new agent a user creates
 *   gets a fresh onboarding. Absence of a row (404 -> `data === null`) signals
 *   a legacy implementation that was minted before the flow existed — the
 *   route guard treats those as already complete.
 * - AI-agent name and prompt intentionally do not live here: the Identity step
 *   writes directly to `KnowledgeBase.one_word_description` /
 *   `short_description`. This hook only persists *that the user finished the
 *   Identity step*, not the values.
 * - Step IDs in the UI (`gs_step_1..N`) are decoupled from the DB's semantic
 *   field names so the checklist can be reordered without migrating data.
 */

// Source of truth for the GettingStarted checklist. Edit this list to
// add/remove/reorder steps. Each ID must have matching `steps.<id>.title` and
// `steps.<id>.description` entries in every translation file, plus a mapping
// in `STEP_SEMANTIC_NAME` and `STEP_COMPLETED_FIELD` below.
export const GS_STEP_IDS = [
  'gs_step_1',
  'gs_step_2',
  'gs_step_3',
  'gs_step_4',
  'gs_step_5',
] as const;
export type GsStepId = (typeof GS_STEP_IDS)[number];

/**
 * Onboarding mode, persisted on the progress row:
 *   - `automatic`: default / the URL-first screen and the AI-driven flow behind it
 *   - `manual`:    user clicked Skip on the URL-first screen — the 4-step manual
 *                  checklist takes over instead
 *
 * The old three-value enum had a separate `choice` state for "user hasn't
 * picked yet", but now that automatic and manual share the same first screen
 * (the URL form, with Skip branching off to manual) there's no pre-choice
 * phase in the UI. `normalizeGsMode` below coerces legacy `choice` rows
 * coming back from the backend so old data still renders correctly — we
 * never write `choice` from the frontend.
 */
export type GsMode = 'automatic' | 'manual';

/** What the backend might actually return for `mode`, including legacy rows. */
type GsModeDb = GsMode | 'choice';

/**
 * Coerce the raw DB value into the narrower UI type. `choice` → `automatic`
 * because both now render the same URL-first screen and the default for new
 * rows is effectively "let the user pick a branch from that screen".
 */
function normalizeGsMode(raw: GsModeDb | undefined): GsMode {
  return raw === 'manual' ? 'manual' : 'automatic';
}

export interface GettingStartedChecklistItem {
  id: GsStepId;
  title: string;
  /**
   * Plain-text description shown when the step is expanded. Ignored when
   * `expandedContent` is supplied — steps with custom UI replace the description.
   */
  description: string;
  completed: boolean;
  skipped: boolean;
  /**
   * Optional custom node rendered in place of `description` when the step is
   * expanded. Used by steps that need an inline form (e.g. Identity).
   */
  expandedContent?: ReactNode;
  /**
   * When set, the checklist replaces the default "Skip step" button with a
   * primary button labeled with this string. Clicking it marks the step as
   * complete (not skipped). Used by steps where progressing forward is the
   * expected action (e.g. Identity → Continue).
   */
  primaryActionLabel?: string;
  /**
   * When true, the checklist renders a "Back" text link next to the primary
   * button. By default it un-completes the previous step in the list and
   * re-expands it; supply `onBackAction` to override that behavior (e.g. on
   * the first step, where "Back" should return to the URL-first entry screen).
   */
  showBackAction?: boolean;
  /**
   * Optional override for the Back button's click handler. When provided, the
   * checklist calls this instead of un-completing the previous step. Used by
   * the first step to bail out of the checklist entirely.
   */
  onBackAction?: () => void;
  /**
   * Optional override for the primary button's click handler. When provided,
   * the checklist calls this instead of marking the step complete — the step
   * owner is then responsible for running any side effects (e.g. firing a
   * mutation) and advancing the flow. Leave unset for the default behavior.
   *
   * May return a Promise; while it's pending, the checklist disables the
   * Continue/Finish button and shows a spinner so the click can't double-fire
   * during async validation/mutation work.
   */
  onPrimaryAction?: () => void | Promise<void>;
}

type SemanticStepName = 'identity' | 'knowledge_base' | 'external_apps' | 'deployment' | 'pricing';

export interface GettingStartedProgressDto {
  // Incoming DB value may include the legacy `choice`; UI consumers should
  // always read `mode` off the hook (which normalizes) rather than off this
  // raw DTO.
  mode: GsModeDb;
  identity_step_completed: boolean;
  knowledge_base_step_completed: boolean;
  external_apps_step_completed: boolean;
  deployment_step_completed: boolean;
  pricing_step_completed: boolean;
  skipped_steps: SemanticStepName[];
  automatic_flow_completed: boolean;
  automatic_low_quality_reason: string | null;
  automatic_source_url: string | null;
  automatic_source_type?: 'url' | 'description' | null;
  external_apps_selection: string[];
  deployment_selection: string[];
  external_apps_other_text: string;
  deployment_other_text: string;
  // Server-derived onboarding theme: 'discord' when the founding user's attribution
  // (or their Deployment-step channel pick) shows Discord intent. Read-only.
  onboarding_brand: OnboardingBrand;
  // Optional because a new bundle can be served by a pod that predates the field.
  is_owner?: boolean;
}

export type OnboardingBrand = 'default' | 'discord';

type CompletedField =
  | 'identity_step_completed'
  | 'knowledge_base_step_completed'
  | 'external_apps_step_completed'
  | 'deployment_step_completed'
  | 'pricing_step_completed';

const STEP_SEMANTIC_NAME: Record<GsStepId, SemanticStepName> = {
  gs_step_1: 'identity',
  gs_step_2: 'knowledge_base',
  gs_step_3: 'external_apps',
  gs_step_4: 'deployment',
  gs_step_5: 'pricing',
};

const STEP_COMPLETED_FIELD: Record<GsStepId, CompletedField> = {
  gs_step_1: 'identity_step_completed',
  gs_step_2: 'knowledge_base_step_completed',
  gs_step_3: 'external_apps_step_completed',
  gs_step_4: 'deployment_step_completed',
  gs_step_5: 'pricing_step_completed',
};

function useProgressQuery() {
  return useQuery<GettingStartedProgressDto | null>({
    queryKey: [QueryKey.gettingStarted.progress],
  });
}

/**
 * Route-guard helper used by `DefaultLayout`. Legacy implementations (no row)
 * are treated as already complete — they skip the Getting Started flow.
 */
export function useIsGettingStartedComplete(): {
  isLoading: boolean;
  isComplete: boolean;
} {
  const { data, isLoading } = useProgressQuery();
  if (isLoading) return { isLoading: true, isComplete: false };
  if (!data) return { isLoading: false, isComplete: true };
  // `=== false`, not `!`: a new bundle can hit an old pod that omits is_owner, and that must not free owners from their own onboarding.
  if (data.is_owner === false) return { isLoading: false, isComplete: true };
  const mode = normalizeGsMode(data.mode);
  if (mode === 'manual') {
    const allDone = GS_STEP_IDS.every((id) => {
      const completedField = STEP_COMPLETED_FIELD[id];
      const semantic = STEP_SEMANTIC_NAME[id];
      return data[completedField] === true || data.skipped_steps.includes(semantic);
    });
    return { isLoading: false, isComplete: allDone };
  }
  // `automatic` (the default): completion hinges on the scrape-and-post-step
  // wizard having been walked to its Finish button.
  return { isLoading: false, isComplete: data.automatic_flow_completed };
}

export interface UseGettingStartedOnboardingResult {
  data: GettingStartedProgressDto | null;
  isLoading: boolean;
  steps: { id: GsStepId; completed: boolean; skipped: boolean }[];
  completed_count: number;
  total_count: number;
  mode: GsMode;
  isAutomaticComplete: boolean;
  isSkipPending: boolean;
  externalAppsSelection: Set<string>;
  deploymentSelection: Set<string>;
  externalAppsOtherText: string;
  deploymentOtherText: string;
  /** The last write of the External apps free-text field was refused or never reached the server. */
  externalAppsOtherTextSaveFailed: boolean;
  skipStep: (stepId: GsStepId, skip: boolean) => void;
  completeStep: (stepId: GsStepId, complete: boolean) => void;
  setMode: (mode: GsMode) => void;
  setAutomaticComplete: (complete: boolean) => void;
  toggleExternalApp: (appId: string) => void;
  toggleDeploymentChannel: (channelId: string) => void;
  rememberSelection: (
    target: 'deployment' | 'external_apps',
    id: string,
    otherText?: string,
  ) => void;
  setExternalAppsOtherText: (text: string) => void;
  setDeploymentOtherText: (text: string) => void;
}

export function useGettingStartedOnboarding(): UseGettingStartedOnboardingResult {
  const queryClient = useQueryClient();
  const { data = null, isLoading } = useProgressQuery();
  const { capture } = usePostHogAnalytics();
  const pendingFieldsRef = useRef(
    new Map<keyof GettingStartedProgressDto, { count: number; confirmedValue: unknown }>(),
  );
  /**
   * Last value the server confirmed for each free-text field.
   *
   * `onMutate` derives its rollback baseline by reading the cache, which is right for the pills:
   * nothing writes them outside the mutation. It is wrong for these two, because the setters below
   * write every keystroke into the cache to keep the input responsive — so by the time the debounced
   * PATCH fires, the "previous" value in the cache is already the unsent one. Reconciling against
   * that makes a rejected write a no-op, and the value the server refused stays on screen looking
   * stored. Seeded from the hydrated value on first edit, then advanced only by a server response.
   */
  const confirmedOtherTextRef = useRef(new Map<OtherTextField, string>());
  /** The free-text field whose last write failed, cleared as soon as the user edits it again. */
  const [otherTextSaveFailed, setOtherTextSaveFailed] = useState<OtherTextField | null>(null);
  /**
   * Whether this hook's component is still on screen. The unmount cleanup flushes the debounced
   * write on the way out, so a Continue clicked mid-typing fires its PATCH *after* the step is gone
   * — and `otherTextSaveFailed` above then has no renderer left. Query-core keeps calling
   * `options.onError` after the observer is removed, so the failure arrives; without this flag it
   * would land in state nobody reads and the typed value would vanish in silence.
   */
  const isMountedRef = useRef(true);

  const mutation = useMutation<
    GettingStartedProgressDto,
    Error,
    Partial<GettingStartedProgressDto>
  >({
    mutationKey: [QueryKey.gettingStarted.progress],
    scope: { id: QueryKey.gettingStarted.progress },
    // Optimistic update: apply the delta to the cache immediately so the UI
    // reflects the user's action before the PATCH round-trips. Mirrors the KB
    // autosave pattern in IdentityStep.tsx.
    onMutate: (delta) => {
      const previous = queryClient.getQueryData<GettingStartedProgressDto | null>([
        QueryKey.gettingStarted.progress,
      ]);
      for (const key of Object.keys(delta) as (keyof GettingStartedProgressDto)[]) {
        const pending = pendingFieldsRef.current.get(key);
        if (pending) pending.count += 1;
        else
          pendingFieldsRef.current.set(key, {
            count: 1,
            confirmedValue:
              isOtherTextField(key) && confirmedOtherTextRef.current.has(key)
                ? confirmedOtherTextRef.current.get(key)
                : previous?.[key],
          });
      }
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, ...delta } : prev),
      );
    },
    onSuccess: (updated, variables) => {
      for (const key of Object.keys(variables) as (keyof GettingStartedProgressDto)[]) {
        const confirmed = Object.prototype.hasOwnProperty.call(updated, key)
          ? updated[key]
          : variables[key];
        // Advance the baseline before the `pending` guard: a write can settle while nothing is
        // pending for the key, and the next one still needs to roll back to what the server holds.
        if (isOtherTextField(key)) {
          confirmedOtherTextRef.current.set(key, (confirmed as string | undefined) ?? '');
          // A later write landing clears an earlier one's complaint; the field is stored now.
          setOtherTextSaveFailed((failed) => (failed === key ? null : failed));
        }
        const pending = pendingFieldsRef.current.get(key);
        if (pending) pending.confirmedValue = confirmed;
      }
    },
    // A free-text write is debounced and invisible, so a refusal has to be said out loud — the value
    // is reconciled away by `onSettled` below and would otherwise just disappear from under the user.
    onError: (_error, variables) => {
      const failed = (Object.keys(variables) as string[]).find(isOtherTextField);
      if (!failed) return;
      // Only the External apps field renders an inline message, and only while it is on screen.
      if (failed === 'external_apps_other_text' && isMountedRef.current) {
        setOtherTextSaveFailed(failed);
        return;
      }
      // Otherwise a toast is the only surface left. It outlives the step transition, so it covers
      // every way out (the checklist's Continue, Back, the sidebar, browser back) rather than the
      // one button, and it also carries the Deployment field, which has no inline message at all.
      toast.error(i18n.t('getting_started:external_apps_step.save_error_toast'));
    },
    // Reconcile only after a field's serialized queue settles, preserving newer optimistic intent.
    onSettled: (_updated, _error, variables) => {
      const settledValues = new Map<
        keyof GettingStartedProgressDto,
        { confirmedValue: unknown; submittedValue: unknown }
      >();
      for (const key of Object.keys(variables) as (keyof GettingStartedProgressDto)[]) {
        const pending = pendingFieldsRef.current.get(key);
        if (!pending) continue;
        pending.count -= 1;
        if (pending.count === 0) {
          settledValues.set(key, {
            confirmedValue: pending.confirmedValue,
            submittedValue: variables[key],
          });
          pendingFieldsRef.current.delete(key);
        }
      }

      if (settledValues.size === 0) return;
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (current) => {
          if (!current) return current;
          const reconciled = { ...current };
          for (const [key, { confirmedValue, submittedValue }] of settledValues) {
            if (isEqual(current[key], submittedValue)) {
              Object.assign(reconciled, { [key]: confirmedValue });
            }
          }
          return reconciled;
        },
      );
    },
  });

  const steps = useMemo(
    () =>
      GS_STEP_IDS.map((id) => {
        const completedField = STEP_COMPLETED_FIELD[id];
        const semantic = STEP_SEMANTIC_NAME[id];
        return {
          id,
          completed: data?.[completedField] ?? false,
          skipped: data?.skipped_steps?.includes(semantic) ?? false,
        };
      }),
    [data],
  );

  // Skipped counts toward "done" for the progress bar — same UX as the
  // AI Preview, so the bar fills up as the user makes decisions.
  const completed_count = useMemo(
    () => steps.filter((s) => s.completed || s.skipped).length,
    [steps],
  );

  const skipStep = useCallback(
    (stepId: GsStepId, skip: boolean) => {
      if (!data) return;
      const semantic = STEP_SEMANTIC_NAME[stepId];
      const next = skip
        ? Array.from(new Set([...data.skipped_steps, semantic]))
        : data.skipped_steps.filter((name) => name !== semantic);
      mutation.mutate({ skipped_steps: next });
      // Only fire on an actual skip — `skip: false` is an unskip (today only
      // reachable from internal test paths, but keep the event semantics tight).
      if (skip) {
        capture(PostHogEventEnum.GettingStartedStepSkipped, {
          step_id: stepId,
          step_name: semantic,
        });
      }
    },
    [data, mutation, capture],
  );

  const completeStep = useCallback(
    (stepId: GsStepId, complete: boolean) => {
      mutation.mutate({ [STEP_COMPLETED_FIELD[stepId]]: complete });
      // `complete: false` is the Back button un-completing the previous step
      // so the user can revisit it. That's not a conversion signal — skip it.
      if (complete) {
        capture(PostHogEventEnum.GettingStartedStepCompleted, {
          step_id: stepId,
          step_name: STEP_SEMANTIC_NAME[stepId],
        });
      }
    },
    [mutation, capture],
  );

  const setMode = useCallback(
    (mode: GsMode) => {
      // Bundle the automatic-complete reset into the same PATCH when leaving
      // the automatic flow. Splitting it across two `mutation.mutate` calls
      // produces a race: each call has the same `mutationKey`, so the first
      // PATCH's `onSuccess` returns the server state that still has the
      // old `mode` and overwrites the optimistic `mode: 'manual'` cache,
      // bouncing the UI back to the URL-first screen.
      const delta: Partial<GettingStartedProgressDto> = { mode };
      if (mode !== 'automatic') delta.automatic_flow_completed = false;
      mutation.mutate(delta);
    },
    [mutation],
  );

  const setAutomaticComplete = useCallback(
    (complete: boolean) => {
      mutation.mutate({ automatic_flow_completed: complete });
    },
    [mutation],
  );

  const toggleExternalApp = useCallback(
    (appId: string) => {
      const current = queryClient.getQueryData<GettingStartedProgressDto | null>([
        QueryKey.gettingStarted.progress,
      ]);
      if (!current) return;
      const next = new Set(current.external_apps_selection);
      if (next.has(appId)) next.delete(appId);
      else next.add(appId);
      mutation.mutate({ external_apps_selection: Array.from(next) });
    },
    [queryClient, mutation],
  );

  const toggleDeploymentChannel = useCallback(
    (channelId: string) => {
      const current = queryClient.getQueryData<GettingStartedProgressDto | null>([
        QueryKey.gettingStarted.progress,
      ]);
      if (!current) return;
      const next = new Set(current.deployment_selection);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      mutation.mutate({ deployment_selection: Array.from(next) });
    },
    [queryClient, mutation],
  );

  /**
   * Add-only counterpart to the two toggles above, used by the SaaS remedy hint shown when a
   * scrape fails on a host we can reach another way (`saasHostRouter`).
   *
   * Add-only, not toggle: the hint is a one-way "remember this for later" gesture, so clicking it
   * on a channel the user already picked in the Deployment step must not silently un-pick it.
   *
   * `otherText` carries the product name for remedies that land on the generic `other_api` id
   * (self-hosted Jira / Confluence), which would otherwise reach the post-onboarding checklist as
   * an unlabelled "Other API" row. Only written when the field is still empty so it can never
   * overwrite something the user typed themselves.
   */
  const rememberSelection = useCallback(
    (target: 'deployment' | 'external_apps', id: string, otherText?: string) => {
      if (!data) return;
      const field = target === 'deployment' ? 'deployment_selection' : 'external_apps_selection';
      const otherTextField =
        target === 'deployment' ? 'deployment_other_text' : 'external_apps_other_text';
      const current = data[field];
      const delta: Partial<GettingStartedProgressDto> = {};
      if (!current.includes(id)) delta[field] = [...current, id];
      // `?? ''` because a new bundle can be served by a pod that predates these two columns.
      if (otherText && !(data[otherTextField] ?? '').trim()) delta[otherTextField] = otherText;
      if (Object.keys(delta).length === 0) return;
      mutation.mutate(delta);
    },
    [data, mutation],
  );

  const mutateRef = useRef(mutation);
  mutateRef.current = mutation;
  const debouncedPatchExternalAppsOtherText = useMemo(
    () =>
      debounce(
        (text: string) => mutateRef.current.mutate({ external_apps_other_text: text }),
        OTHER_TEXT_DEBOUNCE_MS,
      ),
    [],
  );
  const debouncedPatchDeploymentOtherText = useMemo(
    () =>
      debounce(
        (text: string) => mutateRef.current.mutate({ deployment_other_text: text }),
        OTHER_TEXT_DEBOUNCE_MS,
      ),
    [],
  );
  // Flush on unmount so a Continue-mid-typing click still persists the typed value.
  useEffect(() => {
    // Re-arm on mount: StrictMode runs this effect twice in development, and the first cleanup
    // would otherwise leave the flag false for the life of the real mount.
    isMountedRef.current = true;
    return () => {
      // Lowered before the flush, not after: the flush fires its PATCH synchronously on the way
      // out, and everything it reports back belongs to a step that is already gone.
      isMountedRef.current = false;
      debouncedPatchExternalAppsOtherText.flush();
      debouncedPatchDeploymentOtherText.flush();
    };
  }, [debouncedPatchExternalAppsOtherText, debouncedPatchDeploymentOtherText]);

  /**
   * Anchor the rollback baseline to the hydrated server value the first time a free-text field is
   * edited. After this keystroke the cache holds unsent text and can no longer supply one.
   */
  const beginOtherTextEdit = useCallback(
    (field: OtherTextField) => {
      setOtherTextSaveFailed((failed) => (failed === field ? null : failed));
      if (confirmedOtherTextRef.current.has(field)) return;
      const current = queryClient.getQueryData<GettingStartedProgressDto | null>([
        QueryKey.gettingStarted.progress,
      ]);
      confirmedOtherTextRef.current.set(field, current?.[field] ?? '');
    },
    [queryClient],
  );

  const setExternalAppsOtherText = useCallback(
    (text: string) => {
      beginOtherTextEdit('external_apps_other_text');
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, external_apps_other_text: text } : prev),
      );
      // Cancel, don't just skip: a debounce from earlier keystrokes would land the half-typed key.
      if (looksLikeSecret(text)) {
        debouncedPatchExternalAppsOtherText.cancel();
        return;
      }
      debouncedPatchExternalAppsOtherText(text);
    },
    [queryClient, debouncedPatchExternalAppsOtherText, beginOtherTextEdit],
  );

  const setDeploymentOtherText = useCallback(
    (text: string) => {
      beginOtherTextEdit('deployment_other_text');
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, deployment_other_text: text } : prev),
      );
      debouncedPatchDeploymentOtherText(text);
    },
    [queryClient, debouncedPatchDeploymentOtherText, beginOtherTextEdit],
  );

  // Memoize the selection Sets so their identity only changes when the
  // underlying array changes. A fresh `new Set(...)` on every render caused
  // downstream effects depending on these (e.g. the completion-navigate
  // effect in GettingStarted.page.tsx) to re-run on unrelated re-renders,
  // and their cleanup functions cancelled in-flight setTimeouts — which is
  // how a user ended up stuck on a blank screen after finishing onboarding.
  const externalAppsSelection = useMemo(
    () => new Set(data?.external_apps_selection ?? []),
    [data?.external_apps_selection],
  );
  const deploymentSelection = useMemo(
    () => new Set(data?.deployment_selection ?? []),
    [data?.deployment_selection],
  );

  return {
    data,
    isLoading,
    steps,
    completed_count,
    total_count: GS_STEP_IDS.length,
    mode: normalizeGsMode(data?.mode),
    isAutomaticComplete: data?.automatic_flow_completed ?? false,
    isSkipPending: mutation.isPending,
    externalAppsSelection,
    deploymentSelection,
    externalAppsOtherText: data?.external_apps_other_text ?? '',
    deploymentOtherText: data?.deployment_other_text ?? '',
    externalAppsOtherTextSaveFailed: otherTextSaveFailed === 'external_apps_other_text',
    skipStep,
    completeStep,
    setMode,
    setAutomaticComplete,
    toggleExternalApp,
    toggleDeploymentChannel,
    rememberSelection,
    setExternalAppsOtherText,
    setDeploymentOtherText,
  };
}
